import { el, setAttr, setText } from "../lib/dom.js";
import { reconcileKeyed } from "../lib/keyed.js";
import { createIcon } from "../render/icon.js";
import type { RoomSnapshot } from "../workspace/schema.js";
import css from "./Shell.module.css";

export type RoomBarCallbacks = {
  select: (id: string) => void;
  rename: (id: string, name: string) => void;
  create: () => void;
  remove: (id: string) => void;
};

type Tab = {
  el: HTMLButtonElement;
  name: HTMLElement;
  count: HTMLElement;
  input: HTMLInputElement;
};

/**
 * The room switcher. A tab is kept alive across updates rather than rebuilt, so
 * a rename in progress is not destroyed by an unrelated change arriving from
 * another device.
 */
export class RoomBar {
  readonly el: HTMLElement;
  readonly #tabs = new Map<string, Tab>();
  readonly #list: HTMLElement;
  readonly #deleteButton: HTMLButtonElement;
  readonly #callbacks: RoomBarCallbacks;
  #activeId: string | null = null;

  constructor(callbacks: RoomBarCallbacks) {
    this.#callbacks = callbacks;
    this.#list = el("div", { class: css.rooms ?? "", "data-role": "rooms" });

    const newButton = el(
      "button",
      { type: "button", class: css.iconButton ?? "", "data-role": "new-room", title: "New room" },
      [createIcon("plus", 13)],
    );
    newButton.addEventListener("click", () => callbacks.create());

    this.#deleteButton = el(
      "button",
      {
        type: "button",
        class: css.iconButton ?? "",
        "data-role": "delete-room",
        title: "Delete this room",
        hidden: true,
      },
      [createIcon("trash", 13)],
    );
    this.#deleteButton.addEventListener("click", () => {
      if (this.#activeId !== null) callbacks.remove(this.#activeId);
    });

    this.el = el("div", { class: css.rooms ?? "" }, [
      this.#list,
      newButton,
      this.#deleteButton,
    ]);
  }

  update(rooms: readonly RoomSnapshot[], activeId: string | null): void {
    this.#activeId = activeId;
    reconcileKeyed(this.#list, rooms, this.#tabs, {
      key: (room) => room.id,
      create: (room) => this.#createTab(room),
      update: (tab, room) => {
        setAttr(tab.el, "data-room-name", room.name);
        setAttr(tab.el, "data-active", String(room.id === activeId));
        setText(tab.name, room.name);
        setText(tab.count, String(room.windows.length));
      },
    });
    this.#deleteButton.hidden = rooms.length < 2 || activeId === null;
  }

  #createTab(room: RoomSnapshot): Tab {
    const name = el("span", { class: css.roomName ?? "" });
    const count = el("span", { class: css.roomCount ?? "" });
    // Every tab owns a rename box, but only the one being renamed advertises
    // itself as such — a hidden control should not answer to its own name.
    const input = el("input", { class: css.roomRename ?? "", hidden: true });

    const button = el(
      "button",
      { type: "button", class: css.room ?? "", "data-room": room.id },
      [name, count, input],
    );

    const stopRename = (commit: boolean): void => {
      if (input.hidden) return;
      const next = input.value.trim();
      input.hidden = true;
      input.removeAttribute("data-role");
      name.hidden = false;
      count.hidden = false;
      if (commit && next !== "") this.#callbacks.rename(room.id, next);
    };

    button.addEventListener("click", () => {
      if (input.hidden) this.#callbacks.select(room.id);
    });
    button.addEventListener("dblclick", () => {
      input.value = name.textContent ?? "";
      input.hidden = false;
      input.setAttribute("data-role", "room-rename");
      name.hidden = true;
      count.hidden = true;
      input.focus();
      input.select();
    });
    input.addEventListener("click", (event) => event.stopPropagation());
    input.addEventListener("dblclick", (event) => event.stopPropagation());
    input.addEventListener("blur", () => stopRename(true));
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") input.blur();
      if (event.key === "Escape") stopRename(false);
    });

    return { el: button, name, count, input };
  }
}
